import { Component, OnInit, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import GridsConfig from "@app/common/grid.config";
import { catchError, map, tap, startWith, mergeMap } from "rxjs/operators";
import { interval, of, Subscription } from "rxjs";
import {
  GroupedMigrationMetrics,
  Migration,
  MigrationMetric,
} from "@app/interface/interfaces";
import { MigrationStore } from "@app/services/migration.store";
import { ApiService } from "@app/services/api.service";
import { MigrationService } from "@app/services/migration.service";
import { MigrationStatus } from "@app/common/constants";
import { OverallMetrics } from "@app/interface/models";
import { UtilsService } from "@app/services/utils.service";

@Component({
  selector: "app-migration-details",
  templateUrl: "./migration-details.component.html",
  styleUrls: ["./migration-details.component.scss"],
  standalone: false,
})
export class MigrationDetailsComponent implements OnInit {
  // Using inject() for dependency injection
  private readonly apiService = inject(ApiService);
  private readonly migrationStore = inject(MigrationStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly migrationService = inject(MigrationService);
  readonly utilsService = inject(UtilsService);

  private pollingSubscription: Subscription | null = null;

  MigrationStatus = MigrationStatus;

  migrationId: number | null = null;
  migration: Migration | null = null;

  groupedMetrics: GroupedMigrationMetrics = {};
  overallMetrics: OverallMetrics = new OverallMetrics();
  migrationMetrics: MigrationMetric[] = [];
  entityTypes: string[] = [];

  loading = true;
  firstLoadingFinished = false;
  error = false;

  // Column definition for the tables
  displayedColumns: string[] = ["status", "count"];

  // For responsive grid
  cols = 4;
  rowHeight = "300px";

  constructor() {}

  ngOnDestroy() {
    this.stopPolling();
  }

  private validateRoute() {
    const idParam = this.route.snapshot.paramMap.get("id");
    if (idParam === null || isNaN(Number(idParam))) {
      this.router.navigate(["/"]);
    }
    this.migrationId = Number(idParam);
    this.migrationStore.setCurrentMigrationId(String(this.migrationId));
  }

  ngOnInit(): void {
    // validate route param migration id
    this.validateRoute();

    // set upp grid
    this.setUpGridLayout();

    //First fetch migration data
    this.getMigration(this.migrationId).then(() => {
      // After initial fetch completes, check if we need to start polling
      if (
        this.migration &&
        this.migration.progressPercentage < 100 &&
        ![
          MigrationStatus.migrated,
          MigrationStatus.exception,
          MigrationStatus.reject,
        ].includes(this.migration.status)
      ) {
        this.startPolling();
      } else {
        //in case finished then get metrics
        if (
          this.migration?.status != MigrationStatus.reject &&
          this.migration?.status != MigrationStatus.fixed
        ) {
          this.getMigrationMetric(this.migrationId);
        }
      }
      this.firstLoadingFinished = true;
    });
  }

  getTotalCount(entityType: string): number {
    return this.groupedMetrics[entityType].reduce(
      (sum, metric) => sum + metric.count,
      0,
    );
  }

  startPolling(): void {
    // Stop any existing polling
    this.stopPolling();

    // Start new polling
    this.pollingSubscription = interval(10000)
      .pipe(
        // Start immediately
        startWith(0),

        // Fetch migration data and map to null
        mergeMap(() => {
          // First get the migration data
          this.getMigration(this.migrationId);
          // Return an observable that completes immediately
          return of(null);
        }),

        // Check conditions after each fetch
        tap({
          next: () => {
            if (
              this.migration &&
              (this.migration.progressPercentage >= 100 ||
                this.migration.status === MigrationStatus.migrated ||
                this.migration.status === MigrationStatus.exception)
            ) {
              // If conditions are met, complete the subscription immediately
              this.stopPolling();

              // Execute finalization logic immediately
              if (this.migration?.status == MigrationStatus.migrated) {
                this.getMigrationMetric(this.migrationId);
              }
            }
          },
        }),
      )
      .subscribe();
  }

  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  getMigration(migrationId: number | null): Promise<Migration | null> {
    if (!migrationId) {
      return Promise.resolve(null);
    }

    return new Promise<Migration | null>((resolve) => {
      this.changeLoadingState(true);
      this.apiService
        .getMigrationDetails(migrationId)
        .pipe(
          map((migration: Migration) =>
            this.migrationService.processMigrationRecord(migration),
          ),
          tap((processedMigration: Migration) => {
            this.migration = processedMigration;
            this.changeLoadingState(false);
            resolve(processedMigration);
          }),
          catchError((error) => {
            this.changeLoadingState(false);
            resolve(null);
            return of(null);
          }),
        )
        .subscribe();
    });
  }

  getMigrationMetric(
    migrationId: number | null,
  ): Promise<MigrationMetric[] | null> {
    if (!migrationId) {
      return Promise.resolve(null);
    }
    return new Promise<MigrationMetric[] | null>((resolve) => {
      this.changeLoadingState(true);
      this.apiService
        .getMigrationMetrics(migrationId)
        .pipe(
          tap((migrationMetrics: MigrationMetric[]) => {
            this.migrationMetrics = migrationMetrics;
            this.groupedMetrics =
              this.migrationService.groupMigrationMetricsByEntityType(
                migrationMetrics,
              );
            this.entityTypes = Object.keys(this.groupedMetrics);
            this.overallMetrics = this.migrationService.processOverallMetrics(
              this.groupedMetrics,
            );
            this.changeLoadingState(false);
            resolve(migrationMetrics);
          }),
          catchError((error) => {
            this.changeLoadingState(false);
            resolve(null);
            return of(null);
          }),
        )
        .subscribe();
    });
  }

  getMigrationStatusLabel() {
    if (this.migration?.status == MigrationStatus.in_progress) {
      return `${this.migration?.status}- ${this.migration?.statusLabel}`;
    }
    return this.migration?.status;
  }
  getTooltipText() {
    return `Migration status: ${this.migration?.status || ""} ${
      this.migration?.progress
    }`;
  }

  /* PRIVATE */
  private changeLoadingState(state: boolean) {
    this.loading = state;
    this.migrationStore.setLoading(state);
  }

  // setup grid layout
  private setUpGridLayout() {
    // Update grid based on screen size
    this.updateGridLayout();

    // Listen for window resize events to update grid
    window.addEventListener("resize", () => {
      this.updateGridLayout();
    });
  }

  // Update the grid layout method for responsive design
  private updateGridLayout(): void {
    const width = window.innerWidth;
    if (width < 600) {
      this.cols = 1;
      this.rowHeight = "300px";
    } else if (width < 960) {
      this.cols = 2;
      this.rowHeight = "280px";
    } else if (width < 1280) {
      this.cols = 3;
      this.rowHeight = "280px";
    } else {
      this.cols = 4; // Show 4 tiles per row on larger screens
      this.rowHeight = "280px";
    }
  }
}
